// Messaggi — layout mockup-aligned: tabella threads + drawer destro con
// bubble + composer. Spec ref: mockups/Messaggi.html.
//
// Funzionalita' attive:
// - KPI header (totali + per canale)
// - Saved views basic: Tutti / Inviati oggi / Da rispondere (filter
//   client-side sulla lista threads)
// - Filterbar: search + canale chip
// - Tabella: thread per cliente con last message preview + status + operatore
// - Drawer destro: header cliente + meta + thread bubbles + composer
// - Composer: textarea + channel toggle WA/Email
//   - WA: log message + open wa.me in nuova tab
//   - Email: log + Resend via send-email standalone endpoint (TODO M5)
//
// Funzionalita' SKIP per ora:
// - Schedula invio
// - Templates UI in composer
// - Bulk actions
// - SMS
// - Inbound webhook tracking (M5 separato)
// - Operatore filter, Periodo filter, Status filter
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { useMessageThreads, useMessages } from '../hooks/useMessages'
import { useMe } from '../hooks/useAuth'
import { useClient } from '../hooks/useClient'
import { useUsersList } from '../hooks/useComments'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import { NewChatModal } from '../components/NewChatModal'
import {
  deleteConversationAttachment,
  logMessage,
  markNotificationRead,
  uploadConversationAttachment,
} from '../lib/api'
import type {
  ConversationRow,
  MessageAttachment,
  MessageRow,
  NotificationRow,
  ThreadRow,
} from '../lib/api'
import { useNotifications } from '../hooks/useNotifications'
import {
  useAddConversationParticipants,
  useConversation,
  useConversationMessages,
  useConversations,
  useDeleteConversation,
  useKickParticipant,
  useLeaveConversation,
  useMarkConversationRead,
  usePinChat,
  usePinMessage,
  useRenameConversation,
  useSendConversationMessage,
} from '../hooks/useConversations'

export const Route = createFileRoute('/messaggi')({
  validateSearch: (s: Record<string, unknown>): { conversation?: string } => ({
    conversation:
      typeof s.conversation === 'string' ? s.conversation : undefined,
  }),
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: MessaggiPage,
})

type SavedView = 'all' | 'groups' | 'da_rispondere'
type ChannelFilter = 'all' | 'whatsapp' | 'email'
type MainTab = 'tutti' | 'messaggi' | 'notifiche'

// Voce della timeline cronologica unificata del tab "Tutti".
type TimelineItem =
  | { kind: 'conv'; ts: number; conv: ConversationRow }
  | { kind: 'thread'; ts: number; thread: ThreadRow }
  | { kind: 'notif'; ts: number; notif: NotificationRow }

function MessaggiPage() {
  const navigate = useNavigate()
  const routeSearch = Route.useSearch()
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [selectedConvId, setSelectedConvId] = useState<string | null>(
    routeSearch.conversation ?? null,
  )
  const [mainTab, setMainTab] = useState<MainTab>('messaggi')
  const [view, setView] = useState<SavedView>('all')
  const [search, setSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all')
  // Filtri backend
  const [sentByFilter, setSentByFilter] = useState<string | undefined>(undefined)
  const [periodoFrom, setPeriodoFrom] = useState<string | undefined>(undefined)
  const [periodoTo, setPeriodoTo] = useState<string | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [newChatOpen, setNewChatOpen] = useState(false)

  const convsQ = useConversations()
  const conversations = convsQ.data?.items ?? []
  const markConvRead = useMarkConversationRead()
  const pinChatMut = usePinChat()
  const notifQ = useNotifications(false, 30, 'all')
  const notifItems: NotificationRow[] = notifQ.data?.items ?? []
  const notifUnread = notifQ.data?.unread_count ?? 0
  const convUnread = conversations.reduce((n, c) => n + (c.unread_count || 0), 0)

  // Segna letta la conversazione quando viene aperta (clear unread).
  useEffect(() => {
    if (selectedConvId) markConvRead.mutate(selectedConvId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConvId])

  // Deep-link ?conversation=ID (da bolla topbar / NewChatModal): apri il drawer
  // anche quando siamo GIÀ su /messaggi (useState legge la search solo al mount).
  useEffect(() => {
    if (routeSearch.conversation) {
      setSelectedConvId(routeSearch.conversation)
      setSelectedClientId(null)
    }
  }, [routeSearch.conversation])

  const { data, isLoading, isError } = useMessageThreads({
    limit: 200,
    sent_by: sentByFilter,
    sent_at_from: periodoFrom ? new Date(periodoFrom).toISOString() : undefined,
    sent_at_to: periodoTo
      ? new Date(periodoTo + 'T23:59:59').toISOString()
      : undefined,
    status: statusFilter,
  })

  const usersQuery = useUsersList(undefined)
  const allItems = data?.items ?? []

  const filtered = useMemo(() => {
    let arr = allItems
    if (view === 'groups') {
      // 'Gruppi' = solo conversazioni interne di gruppo: nessun thread cliente.
      arr = []
    } else if (view === 'da_rispondere') {
      // Heuristic: thread con 1 solo messaggio outbound + status sent/letto (no risposta tracked).
      // Quando inbound webhook attivo, riformuliamo.
      arr = arr.filter((t) => t.message_count === 1)
    }
    if (channelFilter !== 'all') {
      arr = arr.filter((t) => t.last_message_kind === channelFilter)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      arr = arr.filter(
        (t) =>
          t.client_name.toLowerCase().includes(q) ||
          t.last_message_body_preview.toLowerCase().includes(q) ||
          (t.last_message_subject?.toLowerCase().includes(q) ?? false) ||
          (t.last_message_property_address?.toLowerCase().includes(q) ?? false),
      )
    }
    return arr
  }, [allItems, view, channelFilter, search])

  const convFiltered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let arr = !q
      ? conversations
      : conversations.filter(
          (c) =>
            c.title.toLowerCase().includes(q) ||
            (c.last_message_preview ?? '').toLowerCase().includes(q) ||
            // Trova la chat 1-a-1 cercando il nome del collega tra i partecipanti.
            c.participants.some((p) => (p.name ?? '').toLowerCase().includes(q)),
        )
    // Stesso filtro 'view': 'groups' = solo conversazioni di gruppo,
    // 'da_rispondere' = conversazioni con messaggi non letti da me (unread>0).
    if (view === 'groups') {
      arr = arr.filter((c) => c.is_group === true)
    } else if (view === 'da_rispondere') {
      arr = arr.filter((c) => (c.unread_count ?? 0) > 0)
    }
    // Chat con messaggi prima (data desc); chat vuote (contatti seedati) dopo
    // (ordine alfabetico per titolo/nome collega).
    return [...arr].sort((a, b) => {
      const at = a.last_message_at ? new Date(a.last_message_at).getTime() : null
      const bt = b.last_message_at ? new Date(b.last_message_at).getTime() : null
      if (at != null && bt != null) return bt - at
      if (at != null) return -1
      if (bt != null) return 1
      return a.title.localeCompare(b.title)
    })
  }, [conversations, search, view])

  // Tab "Tutti": interlaccio cronologico vero — conversazioni interne, thread
  // cliente e notifiche fusi in una sola lista ordinata per timestamp (desc).
  // Riusa convFiltered/filtered (già filtrati per view/canale/search); le
  // notifiche sono filtrate solo per testo di ricerca.
  const timeline = useMemo<TimelineItem[]>(() => {
    const out: TimelineItem[] = []
    for (const c of convFiltered) {
      // Chat vuote (contatti seedati senza messaggi) fuori dal feed cronologico.
      if (!c.last_message_at) continue
      const t = new Date(c.last_message_at).getTime()
      out.push({ kind: 'conv', ts: Number.isFinite(t) ? t : 0, conv: c })
    }
    for (const th of filtered) {
      const t = new Date(th.last_message_at).getTime()
      out.push({ kind: 'thread', ts: Number.isFinite(t) ? t : 0, thread: th })
    }
    const q = search.trim().toLowerCase()
    for (const n of notifItems) {
      if (
        q &&
        !(
          n.title.toLowerCase().includes(q) ||
          (n.body ?? '').toLowerCase().includes(q)
        )
      )
        continue
      const t = new Date(n.created_at).getTime()
      out.push({ kind: 'notif', ts: Number.isFinite(t) ? t : 0, notif: n })
    }
    out.sort((a, b) => b.ts - a.ts)
    return out
  }, [convFiltered, filtered, notifItems, search])

  // Tab "Messaggi": conversazioni + thread fusi in una lista unica. Le chat
  // fissate (pinned) vanno in cima; il resto per ultimo messaggio (desc).
  type MsgRow =
    | { kind: 'conv'; id: string; pinned: boolean; ts: number; conv: ConversationRow }
    | { kind: 'thread'; id: string; pinned: boolean; ts: number; thread: ThreadRow }
  const messaggiRows = useMemo<MsgRow[]>(() => {
    const rows: MsgRow[] = []
    for (const c of convFiltered) {
      rows.push({
        kind: 'conv', id: c.id, pinned: !!c.pinned,
        ts: c.last_message_at ? new Date(c.last_message_at).getTime() : 0, conv: c,
      })
    }
    for (const t of filtered) {
      rows.push({
        kind: 'thread', id: t.client_id, pinned: !!t.pinned,
        ts: t.last_message_at ? new Date(t.last_message_at).getTime() : 0, thread: t,
      })
    }
    rows.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
      return b.ts - a.ts
    })
    return rows
  }, [convFiltered, filtered])

  // KPI
  const total = allItems.length
  const waCount = allItems.filter((t) => t.last_message_kind === 'whatsapp').length
  const mailCount = allItems.filter((t) => t.last_message_kind === 'email').length
  const today = new Date().toISOString().slice(0, 10)
  const todayCount = allItems.filter((t) => t.last_message_at.slice(0, 10) === today).length
  const daRispCount = allItems.filter((t) => t.message_count === 1).length
  // Conteggi badge saved-view = thread + conversazioni interne, così i numeri
  // riflettono la lista realmente mostrata (la lista fonde conv + thread).
  const convGroupsCount = conversations.filter((c) => c.is_group === true).length
  const convDaRispCount = conversations.filter((c) => (c.unread_count ?? 0) > 0).length
  const allTabCount = conversations.length + total
  const groupsTabCount = convGroupsCount
  const daRispTabCount = daRispCount + convDaRispCount

  if (isLoading) return <CardSkeleton />
  if (isError) {
    return (
      <EmptyState
        icon="⚠️"
        title="Errore caricamento messaggi"
        body="Riprova fra qualche secondo."
      />
    )
  }

  return (
    <>
      {/* CRUMB */}
      <div className="crumb-row">
        <div className="breadcrumb">
          <span>Comunicazione</span>
          <span className="sep">/</span>
          <span className="here">Messaggi</span>
        </div>
      </div>

      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="page-title-row">
            <h1>Messaggi</h1>
            <span className="total">
              <b>{total.toLocaleString('it-IT')}</b> conversazioni ·{' '}
              <b>{waCount}</b> WhatsApp · <b>{mailCount}</b> email ·{' '}
              <b>{todayCount}</b> oggi
            </span>
          </div>
        </div>
        <div className="page-actions">
          {/* Le chat 1-a-1 coi colleghi esistono già tutte (seed): per parlare a
              un collega cerca il suo nome nella lista. "Nuovo" = solo gruppo. */}
          <button
            type="button"
            className="btn primary"
            title="Crea una chat di gruppo (per le chat 1-a-1 cerca il nome del collega in lista)"
            onClick={() => setNewChatOpen(true)}
          >
            ✏️ Nuova chat di gruppo
          </button>
        </div>
      </div>
      <NewChatModal
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        groupMode
      />

      {/* MAIN TABS — Tutti / Messaggi / Notifiche */}
      <div className="savedviews" style={{ marginBottom: 'var(--sp-2)' }}>
        <a
          className={`sv-tab${mainTab === 'tutti' ? ' active' : ''}`}
          onClick={() => setMainTab('tutti')}
        >
          Tutti
        </a>
        <a
          className={`sv-tab${mainTab === 'messaggi' ? ' active' : ''}`}
          onClick={() => setMainTab('messaggi')}
        >
          Messaggi {convUnread > 0 && <span className="ct">{convUnread}</span>}
        </a>
        <a
          className={`sv-tab${mainTab === 'notifiche' ? ' active' : ''}`}
          onClick={() => setMainTab('notifiche')}
        >
          Notifiche {notifUnread > 0 && <span className="ct">{notifUnread}</span>}
        </a>
      </div>

      {/* SAVED VIEWS */}
      <div className="savedviews">
        <a
          className={`sv-tab${view === 'all' ? ' active' : ''}`}
          onClick={() => setView('all')}
        >
          Tutti <span className="ct">{allTabCount}</span>
        </a>
        <a
          className={`sv-tab${view === 'groups' ? ' active' : ''}`}
          onClick={() => setView('groups')}
        >
          Gruppi <span className="ct">{groupsTabCount}</span>
        </a>
        <a
          className={`sv-tab${view === 'da_rispondere' ? ' active' : ''}`}
          title="Threads con un solo messaggio inviato — nessuna risposta tracciata ancora"
          onClick={() => setView('da_rispondere')}
        >
          Da rispondere <span className="ct">{daRispTabCount}</span>
        </a>
      </div>

      {/* FILTER BAR */}
      <div className="filterbar">
        <div className="filterbar-row">
          <div className="fb-search">
            <span className="ico">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca cliente, oggetto, indirizzo lotto…"
              aria-label="Ricerca messaggi"
            />
          </div>
          <ChannelChip value={channelFilter} onChange={setChannelFilter} />
          <OperatoreChip
            value={sentByFilter}
            onChange={setSentByFilter}
            users={usersQuery.data?.items ?? []}
          />
          <PeriodoChip
            from={periodoFrom}
            to={periodoTo}
            onChange={(f, t) => {
              setPeriodoFrom(f)
              setPeriodoTo(t)
            }}
          />
          <StatusChip value={statusFilter} onChange={setStatusFilter} />
          {(view !== 'all' ||
            channelFilter !== 'all' ||
            search ||
            sentByFilter ||
            periodoFrom ||
            periodoTo ||
            statusFilter) && (
            <button
              type="button"
              className="fb-clear"
              onClick={() => {
                setView('all')
                setChannelFilter('all')
                setSearch('')
                setSentByFilter(undefined)
                setPeriodoFrom(undefined)
                setPeriodoTo(undefined)
                setStatusFilter(undefined)
              }}
            >
              Reset filtri
            </button>
          )}
        </div>
        <div className="fb-meta">
          <span>
            <span className="strong">{convFiltered.length + filtered.length}</span> di{' '}
            <span className="strong">{conversations.length + total}</span> conversazioni visibili
          </span>
          <div className="right">
            <span>Ordinato per <b>Ultimo messaggio ↓</b></span>
          </div>
        </div>
      </div>

      {/* WORK GRID */}
      <div className="msg-work-grid">
        <div className="table-card">
          {mainTab === 'messaggi' &&
            (messaggiRows.length === 0 ? (
              <div style={{ padding: 'var(--sp-5)', textAlign: 'center' }}>
                <EmptyState
                  icon="💬"
                  title="Nessuna conversazione"
                  body={
                    total === 0 && conversations.length === 0
                      ? 'Apri una chat da un lotto o cliente, oppure invia una proposta WhatsApp/email.'
                      : 'Nessun thread matcha i filtri attuali. Resetta filtri.'
                  }
                />
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Data</th>
                    <th style={{ width: '38%' }}>Nome</th>
                    <th>Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {messaggiRows.map((row) =>
                    row.kind === 'conv' ? (
                      <ConvRowEl
                        key={`conv-${row.conv.id}`}
                        c={row.conv}
                        selected={selectedConvId === row.conv.id}
                        onSelect={() => {
                          setSelectedConvId(row.conv.id)
                          setSelectedClientId(null)
                        }}
                        onTogglePin={() =>
                          pinChatMut.mutate({
                            targetKind: 'conversation',
                            targetId: row.conv.id,
                            pinned: !row.conv.pinned,
                          })
                        }
                      />
                    ) : (
                      <ThreadRowEl
                        key={`client-${row.thread.client_id}`}
                        t={row.thread}
                        selected={
                          selectedClientId === row.thread.client_id && !selectedConvId
                        }
                        onSelect={() => {
                          setSelectedClientId(row.thread.client_id)
                          setSelectedConvId(null)
                        }}
                        onTogglePin={() =>
                          pinChatMut.mutate({
                            targetKind: 'client',
                            targetId: row.thread.client_id,
                            pinned: !row.thread.pinned,
                          })
                        }
                      />
                    ),
                  )}
                </tbody>
              </table>
            ))}
          {mainTab === 'tutti' &&
            (timeline.length === 0 ? (
              <div style={{ padding: 'var(--sp-5)', textAlign: 'center' }}>
                <EmptyState
                  icon="🗂️"
                  title="Niente da mostrare"
                  body="Conversazioni, messaggi e notifiche compariranno qui in ordine cronologico."
                />
              </div>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Data</th>
                    <th style={{ width: '38%' }}>Nome</th>
                    <th>Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {timeline.map((it) => {
                    if (it.kind === 'conv')
                      return (
                        <ConvRowEl
                          key={`conv-${it.conv.id}`}
                          c={it.conv}
                          selected={selectedConvId === it.conv.id}
                          onSelect={() => {
                            setSelectedConvId(it.conv.id)
                            setSelectedClientId(null)
                          }}
                        />
                      )
                    if (it.kind === 'thread')
                      return (
                        <ThreadRowEl
                          key={`client-${it.thread.client_id}`}
                          t={it.thread}
                          selected={
                            selectedClientId === it.thread.client_id &&
                            !selectedConvId
                          }
                          onSelect={() => {
                            setSelectedClientId(it.thread.client_id)
                            setSelectedConvId(null)
                          }}
                        />
                      )
                    return (
                      <NotifRowEl key={`notif-${it.notif.id}`} n={it.notif} />
                    )
                  })}
                </tbody>
              </table>
            ))}
          {mainTab === 'notifiche' && (
            <NotificationsPanel items={notifItems} />
          )}
        </div>

        {selectedConvId ? (
          <ConversationDrawer
            conversationId={selectedConvId}
            onClose={() => setSelectedConvId(null)}
          />
        ) : (
          <ThreadDrawer
            clientId={selectedClientId}
            onClose={() => setSelectedClientId(null)}
            onOpenClient={(id) =>
              void navigate({
                to: '/clienti/$id',
                params: { id },
                search: {} as never,
              })
            }
          />
        )}
      </div>
    </>
  )
}

// ─── Channel chip filter ──────────────────────────────────────────

type UserListItem = { id: string; name: string }

function OperatoreChip({
  value,
  onChange,
  users,
}: {
  value: string | undefined
  onChange: (v: string | undefined) => void
  users: UserListItem[]
}) {
  const hasValue = Boolean(value)
  const display = hasValue
    ? users.find((u) => u.id === value)?.name ?? '—'
    : undefined
  return (
    <label
      className={`fb-chip${hasValue ? ' has-value' : ''}`}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="label">Operatore</span>
      {display && <span className="val">{display}</span>}
      <span className="caret">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          opacity: 0, cursor: 'pointer', border: 0, background: 'transparent', appearance: 'none',
        }}
        aria-label="Operatore"
      >
        <option value="">Tutti</option>
        {users.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>
    </label>
  )
}

function StatusChip({
  value,
  onChange,
}: {
  value: string | undefined
  onChange: (v: string | undefined) => void
}) {
  const hasValue = Boolean(value)
  const labelMap: Record<string, string> = {
    link_generated: 'Link generato',
    sent: 'Inviato',
    delivered: 'Consegnato',
    opened: 'Letto',
    bounced: 'Bounce',
  }
  return (
    <label
      className={`fb-chip${hasValue ? ' has-value' : ''}`}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="label">Status</span>
      {hasValue && <span className="val">{labelMap[value!] ?? value}</span>}
      <span className="caret">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          opacity: 0, cursor: 'pointer', border: 0, background: 'transparent', appearance: 'none',
        }}
        aria-label="Status"
      >
        <option value="">Tutti</option>
        <option value="link_generated">Link generato</option>
        <option value="sent">Inviato</option>
        <option value="delivered">Consegnato</option>
        <option value="opened">Letto</option>
        <option value="bounced">Bounce</option>
      </select>
    </label>
  )
}

function PeriodoChip({
  from,
  to,
  onChange,
}: {
  from: string | undefined
  to: string | undefined
  onChange: (from: string | undefined, to: string | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [fromLocal, setFromLocal] = useState<string>(from ?? '')
  const [toLocal, setToLocal] = useState<string>(to ?? '')
  const hasValue = Boolean(from || to)
  const display = hasValue
    ? `${from ? formatDateShort(from) : '—'}→${to ? formatDateShort(to) : '—'}`
    : undefined

  function apply() {
    onChange(fromLocal || undefined, toLocal || undefined)
    setOpen(false)
  }
  function reset() {
    setFromLocal('')
    setToLocal('')
    onChange(undefined, undefined)
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className={`fb-chip${hasValue ? ' has-value' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="label">Periodo</span>
        {display && <span className="val">{display}</span>}
        <span className="caret">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 'var(--sp-3)',
            minWidth: 240,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Dal</span>
            <input
              type="date"
              value={fromLocal}
              onChange={(e) => setFromLocal(e.target.value)}
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Al</span>
            <input
              type="date"
              value={toLocal}
              onChange={(e) => setToLocal(e.target.value)}
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
            <button type="button" className="btn sm ghost" onClick={reset}>
              Reset
            </button>
            <button type="button" className="btn sm primary" onClick={apply}>
              Applica
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function formatDateShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}`
}

function ChannelChip({
  value,
  onChange,
}: {
  value: ChannelFilter
  onChange: (v: ChannelFilter) => void
}) {
  const hasValue = value !== 'all'
  const display =
    value === 'whatsapp' ? 'WhatsApp' : value === 'email' ? 'Email' : undefined
  return (
    <label
      className={`fb-chip${hasValue ? ' has-value' : ''}`}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="label">Canale</span>
      {hasValue && display && <span className="val">{display}</span>}
      <span className="caret">
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ChannelFilter)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          border: 0,
          background: 'transparent',
          appearance: 'none',
        }}
        aria-label="Canale"
      >
        <option value="all">Tutti</option>
        <option value="whatsapp">WhatsApp</option>
        <option value="email">Email</option>
      </select>
    </label>
  )
}

// ─── Tabella riga ─────────────────────────────────────────────────

function ThreadRowEl({
  t,
  selected,
  onSelect,
  onTogglePin,
  unread = false,
}: {
  t: ThreadRow
  selected: boolean
  onSelect: () => void
  onTogglePin?: () => void
  unread?: boolean
}) {
  const date = formatDate(t.last_message_at)
  const rel = relativeTime(t.last_message_at)
  const isToday = t.last_message_at.slice(0, 10) === new Date().toISOString().slice(0, 10)
  const isWa = t.last_message_kind === 'whatsapp'
  const initials = computeInitials(t.client_name)
  return (
    <tr
      className={selected ? 'selected' : undefined}
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        ...(unread && !selected
          ? { background: 'var(--accent-soft)', borderLeft: '3px solid var(--risk)' }
          : {}),
      }}
    >
      <td>
        <div className="msg-date">
          <span>{date}</span>
          <span className={`rel${isToday ? ' today' : ''}`}>{rel}</span>
        </div>
      </td>
      <td>
        <div className="msg-dest">
          <div
            className="avatar sm"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: avatarColor(t.client_id),
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div className="info">
            <div className="nm">
              C_{String(t.client_short_code).padStart(5, '0')}: {t.client_name}
            </div>
            <div className="role">
              <span className="dest-type">CLIENTE</span>
              <span title={isWa ? 'WhatsApp' : 'Email'}>{isWa ? <SvgWA /> : <SvgMail />}</span>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div className="msg-subj" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="prev" style={{ flex: 1 }}>{t.last_message_body_preview}</div>
          {onTogglePin && (
            <button
              type="button"
              title={t.pinned ? 'Sfissa chat' : 'Fissa chat in alto'}
              onClick={(e) => {
                e.stopPropagation()
                onTogglePin()
              }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: t.pinned ? 1 : 0.35, fontSize: 13 }}
            >
              📌
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Drawer destro ────────────────────────────────────────────────

function DrawerNotifications() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data } = useNotifications(false, 15, 'all')
  const items: NotificationRow[] = data?.items ?? []
  const unreadCount = data?.unread_count ?? 0

  async function handleClick(n: NotificationRow) {
    if (!n.read) {
      await markNotificationRead(n.id)
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
    }
    if (n.target_url) {
      void navigate({ to: n.target_url as never })
    }
  }

  return (
    <div className="msg-drawer-notif">
      <div className="msg-drawer-notif-hint">
        Seleziona una conversazione per aprirla
      </div>
      <div className="msg-drawer-notif-header">
        <span className="title">Notifiche</span>
        {unreadCount > 0 && (
          <span className="badge">{unreadCount}</span>
        )}
      </div>
      {items.length === 0 ? (
        <div className="msg-drawer-notif-empty">Nessuna notifica recente</div>
      ) : (
        <ul className="msg-drawer-notif-list">
          {items.map((n) => (
            <li
              key={n.id}
              className={`msg-drawer-notif-item${n.read ? '' : ' unread'}`}
              onClick={() => void handleClick(n)}
              style={{ cursor: n.target_url ? 'pointer' : 'default' }}
            >
              {!n.read && <span className="unread-dot" aria-hidden="true" />}
              <div className="notif-content">
                <div className="notif-title">{n.title}</div>
                {n.body && <div className="notif-body">{n.body}</div>}
                <div className="notif-time">{relativeTime(n.created_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ThreadDrawer({
  clientId,
  onClose,
  onOpenClient,
}: {
  clientId: string | null
  onClose: () => void
  onOpenClient: (id: string) => void
}) {
  if (!clientId) {
    return (
      <aside className="msg-drawer">
        <DrawerNotifications />
      </aside>
    )
  }
  return <ThreadDrawerContent clientId={clientId} onClose={onClose} onOpenClient={onOpenClient} />
}

// Riga conversazione interna (gruppo) nella lista.
function ConvRowEl({
  c,
  selected,
  onSelect,
  onTogglePin,
}: {
  c: ConversationRow
  selected: boolean
  onSelect: () => void
  onTogglePin?: () => void
}) {
  const hasMsg = !!c.last_message_at
  const ts = c.last_message_at ?? c.updated_at
  const date = hasMsg ? formatDate(ts) : '—'
  const rel = hasMsg ? relativeTime(ts) : ''
  const unread = c.unread_count > 0
  const activeParts = c.participants.filter((p) => !p.left)
  return (
    <tr
      className={selected ? 'selected' : undefined}
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        ...(unread && !selected
          ? { background: 'var(--accent-soft)', borderLeft: '3px solid var(--risk)' }
          : {}),
      }}
    >
      <td>
        <div className="msg-date">
          <span>{date}</span>
          <span className="rel">{rel}</span>
        </div>
      </td>
      <td>
        <div className="msg-dest">
          <div
            className="avatar sm"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: avatarColor(c.id),
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            🗨️
          </div>
          <div className="info">
            <div className="nm" style={unread ? { fontWeight: 700 } : undefined}>
              {c.title}
            </div>
            <div className="role" title={activeParts.map((p) => p.name).join(', ')}>
              <span
                className="dest-type"
                style={{ background: 'oklch(94% 0.04 250)', color: 'oklch(45% 0.15 250)' }}
              >
                CHAT
              </span>
              <span className="mono">{activeParts.length} 👤</span>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div className="msg-subj" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div className="prev" style={{ flex: 1 }}>{c.last_message_preview ?? 'Nessun messaggio'}</div>
          {onTogglePin && (
            <button
              type="button"
              title={c.pinned ? 'Sfissa chat' : 'Fissa chat in alto'}
              onClick={(e) => {
                e.stopPropagation()
                onTogglePin()
              }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: c.pinned ? 1 : 0.35, fontSize: 13 }}
            >
              📌
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// Pannello notifiche (tab Notifiche / Tutti).
function NotificationsPanel({ items }: { items: NotificationRow[] }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  async function handleClick(n: NotificationRow) {
    if (!n.read) {
      await markNotificationRead(n.id)
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    }
    if (n.target_url) void navigate({ to: n.target_url as never })
  }
  if (items.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-4)' }}>
        <EmptyState icon="🔔" title="Nessuna notifica" body="Le notifiche appariranno qui." />
      </div>
    )
  }
  return (
    <ul className="msg-drawer-notif-list" style={{ listStyle: 'none', margin: 0, padding: 'var(--sp-2)' }}>
      {items.map((n) => (
        <li
          key={n.id}
          className={`msg-drawer-notif-item${n.read ? '' : ' unread'}`}
          onClick={() => void handleClick(n)}
          style={{ cursor: n.target_url ? 'pointer' : 'default', padding: 'var(--sp-2)', borderBottom: '1px solid var(--line)' }}
        >
          {!n.read && <span className="unread-dot" aria-hidden="true" />}
          <div className="notif-content">
            <div className="notif-title" style={n.read ? undefined : { fontWeight: 700 }}>
              {n.title}
            </div>
            {n.body && <div className="notif-body">{n.body}</div>}
            <div className="notif-time">{relativeTime(n.created_at)}</div>
          </div>
        </li>
      ))}
    </ul>
  )
}

// Riga notifica nella timeline cronologica del tab "Tutti" (stessa griglia
// 3 colonne Data/Nome/Preview di conv/thread). Click = segna letta + naviga.
function NotifRowEl({ n }: { n: NotificationRow }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const date = formatDate(n.created_at)
  const rel = relativeTime(n.created_at)
  const unread = !n.read
  async function handleClick() {
    if (!n.read) {
      await markNotificationRead(n.id)
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    }
    if (n.target_url) void navigate({ to: n.target_url as never })
  }
  return (
    <tr
      onClick={() => void handleClick()}
      style={{
        cursor: n.target_url ? 'pointer' : 'default',
        ...(unread
          ? { background: 'var(--accent-soft)', borderLeft: '3px solid var(--risk)' }
          : {}),
      }}
    >
      <td>
        <div className="msg-date">
          <span>{date}</span>
          <span className="rel">{rel}</span>
        </div>
      </td>
      <td>
        <div className="msg-dest">
          <div
            className="avatar sm"
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'oklch(94% 0.04 80)',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            🔔
          </div>
          <div className="info">
            <div className="nm" style={unread ? { fontWeight: 700 } : undefined}>
              {n.title}
            </div>
            <div className="role">
              <span
                className="dest-type"
                style={{ background: 'oklch(95% 0.05 80)', color: 'oklch(50% 0.13 80)' }}
              >
                NOTIFICA
              </span>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div className="msg-subj">
          <div className="prev">{n.body ?? ''}</div>
        </div>
      </td>
    </tr>
  )
}

// Drawer conversazione interna di gruppo: bolle + composer + ricerca + aggiungi.
function ConversationDrawer({
  conversationId,
  onClose,
}: {
  conversationId: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: conv } = useConversation(conversationId)
  const [searchQ, setSearchQ] = useState('')
  const { data: msgsData } = useConversationMessages(conversationId, searchQ || undefined)
  const sendMut = useSendConversationMessage(conversationId)
  const addMut = useAddConversationParticipants(conversationId)
  const kickMut = useKickParticipant(conversationId)
  const leaveMut = useLeaveConversation()
  const pinMsgMut = usePinMessage(conversationId)
  const renameMut = useRenameConversation(conversationId)
  const deleteMut = useDeleteConversation()
  const usersQuery = useUsersList(undefined)
  const { data: me } = useMe()
  const myId = me?.id
  const [body, setBody] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const [pending, setPending] = useState<MessageAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setUploading(true)
    try {
      const uploaded: MessageAttachment[] = []
      for (const f of Array.from(files)) {
        uploaded.push(await uploadConversationAttachment(conversationId, f))
      }
      setPending((p) => [...p, ...uploaded])
    } catch {
      // Upload fallito: lascia invariato; l'utente può riprovare.
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function removePending(id: string) {
    setPending((p) => p.filter((a) => a.id !== id))
  }

  const messages = msgsData?.items ?? []
  const messagesAsc = [...messages].sort(
    (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
  )
  // Apri sempre sull'ultimo messaggio (scroll in fondo).
  useEffect(() => {
    const el = bodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, conversationId])
  const title = conv?.title ?? 'Conversazione'
  const participants = conv?.participants?.filter((p) => !p.left) ?? []
  const partIds = new Set(participants.map((p) => p.user_id))
  // Doppia spunta: i miei messaggi sono "letti" quando TUTTI gli altri
  // partecipanti attivi hanno last_read_at >= sent_at. othersReadAt = la
  // lettura più vecchia tra gli altri (null se qualcuno non ha mai letto).
  const othersReadAt = (() => {
    const others = participants.filter((p) => p.user_id !== myId)
    if (others.length === 0) return null
    const times = others.map((p) =>
      p.last_read_at ? new Date(p.last_read_at).getTime() : null,
    )
    if (times.some((t) => t == null)) return null
    return Math.min(...(times as number[]))
  })()
  const allUsers = (usersQuery.data?.items ?? []) as Array<{
    id: string
    name: string
    avatar_url?: string | null
  }>
  const addable = allUsers.filter((u) => !partIds.has(u.id))
  const usersById: Record<
    string,
    { name?: string | null; avatar_url?: string | null }
  > = Object.fromEntries(
    allUsers.map((u) => [u.id, { name: u.name, avatar_url: u.avatar_url }]),
  )
  // 1-a-1 (chat collega senza contesto) = niente aggiungi membri.
  const isGroup = conv?.is_group ?? true
  const isOwner = !!myId && conv?.created_by === myId
  const otherActive = participants.filter((p) => p.user_id !== myId)

  function doRename() {
    const next = window.prompt('Rinomina conversazione', title)
    if (next != null && next.trim()) renameMut.mutate(next.trim())
  }

  function doDelete() {
    if (
      !window.confirm(
        "Eliminare questa conversazione per tutti i partecipanti? L'azione è irreversibile.",
      )
    )
      return
    deleteMut.mutate(conversationId, { onSuccess: () => onClose() })
  }

  function handleLeave() {
    if (isOwner && otherActive.length >= 1) {
      setLeaveOpen(true)
      return
    }
    if (!window.confirm('Uscire da questa conversazione?')) return
    leaveMut.mutate({ id: conversationId }, { onSuccess: () => onClose() })
  }

  function confirmLeaveWithOwner(newOwnerId: string) {
    leaveMut.mutate(
      { id: conversationId, newOwnerId },
      {
        onSuccess: () => {
          setLeaveOpen(false)
          onClose()
        },
      },
    )
  }

  function openContext() {
    if (!conv) return
    if (conv.context_type === 'property' && conv.property_id)
      void navigate({ to: '/lotti/$id', params: { id: conv.property_id }, search: {} as never })
    else if (conv.context_type === 'client' && conv.client_id)
      void navigate({ to: '/clienti/$id', params: { id: conv.client_id }, search: {} as never })
    else if (conv.context_type === 'participation' && conv.participation_id)
      void navigate({ to: '/partecipazioni/$id', params: { id: conv.participation_id }, search: {} as never })
    else if (conv.context_type === 'appointment' && conv.appointment_id)
      void navigate({ to: '/sopralluoghi/$id', params: { id: conv.appointment_id }, search: {} as never })
  }

  function handleSend() {
    const text = body.trim()
    if (!text && pending.length === 0) return
    sendMut.mutate(
      {
        body: text,
        attachment_ids: pending.length ? pending.map((a) => a.id) : undefined,
      },
      {
        onSuccess: () => {
          setBody('')
          setPending([])
        },
      },
    )
  }

  async function handleDeleteAttachment(att: MessageAttachment) {
    if (!window.confirm(`Eliminare l'allegato "${att.filename}"?`)) return
    try {
      await deleteConversationAttachment(conversationId, att.id)
      void qc.invalidateQueries({
        queryKey: ['messages', 'conversation', conversationId],
      })
    } catch {
      // best-effort: se fallisce, l'allegato resta visibile
    }
  }

  return (
    <aside className="msg-drawer">
      <div className="td-head">
        <div className="avatar" style={{ background: avatarColor(conversationId) }}>
          🗨️
        </div>
        <div className="info">
          <div className="nm">
            <span
              onClick={openContext}
              style={{ cursor: conv?.context_type ? 'pointer' : 'default' }}
              title={conv?.context_type ? 'Apri contesto' : undefined}
            >
              {title}
            </span>
          </div>
          <div className="meta">
            👥 {participants.map((p) => p.name || '—').join(', ') || 'Solo tu'}
          </div>
        </div>
        <div className="actions">
          <button type="button" title="Rinomina conversazione" onClick={doRename}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </button>
          {isGroup && (
            <button
              type="button"
              title="Aggiungi membri alla chat"
              onClick={() => setAddOpen((o) => !o)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M19 8v6M22 11h-6" />
              </svg>
            </button>
          )}
          {isGroup && (
            <button
              type="button"
              title="Esci dal gruppo"
              onClick={handleLeave}
              disabled={leaveMut.isPending}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
            </button>
          )}
          <button
            type="button"
            title="Elimina conversazione"
            onClick={doDelete}
            disabled={deleteMut.isPending}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
          </button>
          <button type="button" title="Chiudi" onClick={onClose}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      </div>

      {addOpen && (
        <div className="td-meta-row" style={{ gap: 6, flexDirection: 'column', alignItems: 'stretch' }}>
          <select
            defaultValue=""
            className="filter-select"
            style={{ fontSize: 'var(--fs-12)' }}
            onChange={(e) => {
              const uid = e.target.value
              if (uid) addMut.mutate([uid], { onSuccess: () => setAddOpen(false) })
            }}
          >
            <option value="">+ Aggiungi collega…</option>
            {addable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          {isOwner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {participants
                .filter((p) => p.user_id !== conv?.created_by)
                .map((p) => (
                  <div
                    key={p.user_id}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 'var(--fs-12)' }}
                  >
                    <span>{p.name || '—'}</span>
                    <button
                      type="button"
                      title={`Rimuovi ${p.name || 'membro'}`}
                      onClick={() => {
                        if (window.confirm(`Rimuovere ${p.name || 'questo membro'} dalla chat?`))
                          kickMut.mutate(p.user_id)
                      }}
                      disabled={kickMut.isPending}
                      style={{ color: 'var(--danger, #c0392b)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '2px 8px', background: 'transparent', cursor: 'pointer' }}
                    >
                      Rimuovi
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {leaveOpen && (
        <div className="td-meta-row" style={{ gap: 6 }}>
          <select
            defaultValue=""
            className="filter-select"
            style={{ fontSize: 'var(--fs-12)' }}
            onChange={(e) => {
              const uid = e.target.value
              if (uid) confirmLeaveWithOwner(uid)
            }}
          >
            <option value="">Esci — scegli il nuovo responsabile…</option>
            {otherActive.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name || '—'}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setLeaveOpen(false)}
            style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '2px 8px', background: 'transparent', cursor: 'pointer' }}
          >
            Annulla
          </button>
        </div>
      )}

      <div className="td-meta-row" style={{ gap: 6 }}>
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Cerca nei messaggi…"
          aria-label="Cerca nella chat"
          style={{ width: '100%', fontSize: 'var(--fs-12)', padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
        />
      </div>

      {conv?.pinned_message && (
        <div
          className="td-meta-row"
          style={{ gap: 8, alignItems: 'center', background: 'var(--surface-2, #f5f5f7)', borderLeft: '3px solid var(--accent, #2d6cdf)', padding: '6px 10px' }}
        >
          <span title="Messaggio fissato">📌</span>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById(`msg-${conv.pinned_message!.id}`)
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }}
            style={{ flex: 1, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-12)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <b>{conv.pinned_message.sent_by_name || 'Collega'}:</b>{' '}
            {conv.pinned_message.body.slice(0, 80)}
          </button>
          <button
            type="button"
            title="Sfissa"
            onClick={() => pinMsgMut.mutate(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>
      )}

      <div className="td-body" ref={bodyRef}>
        {messages.length === 0 ? (
          <div className="bubble system">
            {searchQ ? 'Nessun messaggio trovato' : 'Nessun messaggio in questa chat'}
          </div>
        ) : (
          renderBubbles(
            messagesAsc,
            myId,
            usersById,
            othersReadAt,
            handleDeleteAttachment,
            conv?.pinned_message?.id ?? null,
            (messageId: string | null) => pinMsgMut.mutate(messageId),
          )
        )}
      </div>

      <div className="td-composer">
        {pending.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: 6,
            }}
          >
            {pending.map((a) => (
              <div
                key={a.id}
                style={{
                  position: 'relative',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  padding: a.is_image ? 0 : '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  maxWidth: 160,
                }}
              >
                {a.is_image ? (
                  <img
                    src={a.url}
                    alt={a.filename}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: 'cover',
                      borderRadius: 6,
                      display: 'block',
                    }}
                  />
                ) : (
                  <>
                    <span style={{ fontSize: 16 }}>📄</span>
                    <span
                      style={{
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.filename}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  aria-label="Rimuovi allegato"
                  onClick={() => removePending(a.id)}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'var(--ink-1, #333)',
                    color: '#fff',
                    fontSize: 11,
                    lineHeight: '18px',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            // Invio = invia. Shift+Invio = a capo.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder="Scrivi al team… (Invio per inviare, Shift+Invio a capo)"
          disabled={sendMut.isPending}
          rows={3}
          style={{ width: '100%', fontFamily: 'inherit' }}
        />
        {sendMut.isError && (
          <div className="error" style={{ fontSize: 11 }}>
            Errore invio
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          style={{ display: 'none' }}
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || sendMut.isPending}
            title="Allega file o foto"
          >
            {uploading ? 'Caricamento…' : '📎 Allega'}
          </button>
          <button
            type="button"
            className="btn primary sm"
            onClick={handleSend}
            disabled={
              sendMut.isPending || (!body.trim() && pending.length === 0)
            }
          >
            {sendMut.isPending ? 'Invio…' : 'Invia'}
          </button>
        </div>
      </div>
    </aside>
  )
}

function ThreadDrawerContent({
  clientId,
  onClose,
  onOpenClient,
}: {
  clientId: string
  onClose: () => void
  onOpenClient: (id: string) => void
}) {
  const { data: client } = useClient(clientId)
  const { data: msgsData, refetch } = useMessages({
    client_id: clientId,
    page_size: 200,
  })
  const qc = useQueryClient()
  const [body, setBody] = useState('')
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('whatsapp')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string>('')

  const messages = msgsData?.items ?? []
  // Order ASC for chat-like rendering (oldest first).
  const messagesAsc = [...messages].sort(
    (a, b) =>
      new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
  )
  const bodyRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = bodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, clientId])
  const clientName = client
    ? `${client.cognome ?? ''} ${client.nome ?? ''}`.trim() || 'Cliente'
    : 'Caricamento…'
  const phone = client?.telefono ?? null

  async function handleSend() {
    if (!body.trim()) return
    setError('')
    setSending(true)
    try {
      const payload = {
        kind: channel,
        body: body.trim(),
        client_id: clientId,
        status: channel === 'whatsapp' ? 'link_generated' : 'sent',
      } as const
      await logMessage(payload)
      if (channel === 'whatsapp') {
        if (!phone) {
          setError(
            'Telefono cliente mancante — messaggio loggato ma wa.me link non aperto.',
          )
        } else {
          const e164 = phone.replace(/[^0-9+]/g, '').replace(/^\+/, '')
          const url = `https://wa.me/${e164}?text=${encodeURIComponent(body.trim())}`
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      }
      setBody('')
      await refetch()
      void qc.invalidateQueries({ queryKey: ['messages', 'threads'] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore invio')
    } finally {
      setSending(false)
    }
  }

  return (
    <aside className="msg-drawer">
      <div className="td-head">
        <div
          className="avatar"
          style={{ background: avatarColor(clientId) }}
        >
          {computeInitials(clientName)}
        </div>
        <div className="info">
          <div className="nm">
            <span>{clientName}</span>
            {client && (
              <span className="code">
                C_{String(client.short_code).padStart(5, '0')}
              </span>
            )}
          </div>
          <div className="meta">
            {phone && <span className="mono">{phone}</span>}
            {client?.email && (
              <>
                <span className="sep">·</span>
                <span style={{ fontSize: 10 }}>{client.email}</span>
              </>
            )}
          </div>
        </div>
        <div className="actions">
          <button
            type="button"
            title="Apri scheda cliente"
            onClick={() => onOpenClient(clientId)}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinecap="round"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </svg>
          </button>
          <button type="button" title="Chiudi" onClick={onClose}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
      </div>

      <div className="td-meta-row">
        <span>{messages.length} messaggi totali</span>
      </div>

      <div className="td-body" ref={bodyRef}>
        {messages.length === 0 ? (
          <div className="bubble system">Nessun messaggio in questo thread</div>
        ) : (
          renderBubbles(messagesAsc)
        )}
      </div>

      <div className="td-composer">
        <div className="ch-toggle">
          <button
            type="button"
            className={channel === 'whatsapp' ? 'on' : ''}
            onClick={() => setChannel('whatsapp')}
          >
            <SvgWA />
            WhatsApp
          </button>
          <button
            type="button"
            className={channel === 'email' ? 'on' : ''}
            onClick={() => setChannel('email')}
          >
            <SvgMail />
            Email
          </button>
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            channel === 'whatsapp'
              ? 'Scrivi messaggio WhatsApp — apriremo wa.me e logghiamo invio'
              : 'Scrivi email — logghiamo invio (delivery via Resend in M5)'
          }
        />
        {error && (
          <div className="error" style={{ fontSize: 11 }}>
            {error}
          </div>
        )}
        <div className="row">
          <span className="hint">
            {channel === 'whatsapp'
              ? phone
                ? `→ wa.me/${phone.replace(/[^0-9+]/g, '')}`
                : 'Phone mancante — solo log'
              : 'Email log (no delivery automatico)'}
          </span>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => void handleSend()}
            disabled={sending || !body.trim()}
            style={{ marginLeft: 'auto' }}
          >
            {sending ? 'Invio…' : 'Invia'}
          </button>
        </div>
      </div>
    </aside>
  )
}

// myId: se passato (thread interni), i messaggi miei vanno a destra (out),
// quelli del collega a sinistra (in) — convenzione WhatsApp. Per i thread
// cliente myId è undefined → tutti "out" (solo io invio, il cliente non
// risponde nel sistema).
function renderBubbles(
  messagesAsc: MessageRow[],
  myId?: string,
  usersById?: Record<string, { name?: string | null; avatar_url?: string | null }>,
  // Chat interne: timestamp (ms) di lettura più vecchio tra gli altri
  // partecipanti, null se non tutti hanno letto. Se undefined (thread cliente)
  // si usa il vecchio criterio su m.status.
  othersReadAt?: number | null,
  // Se presente, mostra una × per eliminare i propri allegati (solo isMine).
  onDeleteAttachment?: (att: MessageAttachment) => void,
  // Pin messaggio (solo chat interne): id del messaggio fissato + callback.
  pinnedId?: string | null,
  onPin?: (messageId: string | null) => void,
) {
  const out: React.ReactNode[] = []
  let lastDay = ''
  let lastInSender: string | null = null
  for (const m of messagesAsc) {
    const day = formatDayLong(m.sent_at)
    if (day !== lastDay) {
      out.push(
        <div key={`d_${day}`} className="bubble-day">
          {day}
        </div>,
      )
      lastDay = day
      lastInSender = null
    }
    if (m.template_code === 'system_event') {
      out.push(
        <div
          key={m.id}
          className="bubble system"
          style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-3)', alignSelf: 'center' }}
        >
          {m.body}
        </div>,
      )
      continue
    }
    const isMine = myId ? m.sent_by === myId : true
    // Chat di gruppo: mostra mittente sui messaggi altrui quando cambia chi scrive.
    if (!isMine && myId && m.sent_by !== lastInSender) {
      const info = m.sent_by ? usersById?.[m.sent_by] : undefined
      const sName = info?.name || m.sent_by_name || 'Collega'
      // Solo iniziali colorate (niente foto profilo: evita richieste/caricamenti).
      out.push(
        <div
          key={`s_${m.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            margin: '6px 0 1px 4px',
            alignSelf: 'flex-start',
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: avatarColor(m.sent_by ?? sName),
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 8,
              fontWeight: 700,
            }}
          >
            {computeInitials(sName)}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)' }}>
            {sName}
          </span>
        </div>,
      )
    }
    lastInSender = isMine ? null : m.sent_by
    out.push(
      <div id={`msg-${m.id}`} key={m.id} className={`bubble ${isMine ? 'out' : 'in'}`}>
        {m.subject && <span className="subj">{m.subject}</span>}
        {m.attachments && m.attachments.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginBottom: m.body ? 4 : 0,
            }}
          >
            {m.attachments.map((a) => (
              <div
                key={a.id}
                style={{ position: 'relative', maxWidth: a.is_image ? 220 : 240 }}
              >
                {a.is_image ? (
                  <a href={a.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={a.url}
                      alt={a.filename}
                      style={{
                        maxWidth: 220,
                        maxHeight: 220,
                        // w/h noti → riserva lo spazio (no layout shift al load).
                        width: a.width && a.height ? '100%' : undefined,
                        aspectRatio:
                          a.width && a.height
                            ? `${a.width} / ${a.height}`
                            : undefined,
                        borderRadius: 8,
                        display: 'block',
                        objectFit: 'cover',
                      }}
                    />
                  </a>
                ) : (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 8px',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      textDecoration: 'none',
                      color: 'inherit',
                      maxWidth: 240,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>📄</span>
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {a.filename}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                        {formatBytes(a.size_bytes)}
                      </span>
                    </span>
                  </a>
                )}
                {isMine && onDeleteAttachment && (
                  <button
                    type="button"
                    aria-label="Elimina allegato"
                    title="Elimina allegato"
                    onClick={(e) => {
                      e.preventDefault()
                      onDeleteAttachment(a)
                    }}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'var(--ink-1, #333)',
                      color: '#fff',
                      fontSize: 11,
                      lineHeight: '18px',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {m.body}
        <span className="ts">
          {formatTime(m.sent_at)}
          {isMine &&
            (() => {
              // ✓✓ = letto. Chat interne: tutti gli altri hanno letto
              // (othersReadAt >= sent_at). Thread cliente: fallback su status.
              const sentMs = new Date(m.sent_at).getTime()
              const read =
                othersReadAt !== undefined
                  ? othersReadAt != null && othersReadAt >= sentMs
                  : m.status === 'opened' || m.status === 'delivered'
              return (
                <>
                  {' '}
                  <span
                    className="ok-ck"
                    style={read ? { color: '#34b7f1' } : undefined}
                    title={read ? 'Letto' : 'Inviato'}
                  >
                    {read ? '✓✓' : '✓'}
                  </span>
                </>
              )
            })()}
        </span>
        {onPin && (
          <button
            type="button"
            title={pinnedId === m.id ? 'Sfissa messaggio' : 'Fissa messaggio'}
            onClick={() => onPin(pinnedId === m.id ? null : m.id)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: pinnedId === m.id ? 1 : 0.45, fontSize: 11, marginLeft: 4 }}
          >
            📌
          </button>
        )}
      </div>,
    )
  }
  return out
}

// ─── Helpers ──────────────────────────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} · ${hh}:${mi}`
}

function formatDayLong(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const isToday = d.toDateString() === today.toDateString()
  const yest = new Date()
  yest.setDate(yest.getDate() - 1)
  const isYest = d.toDateString() === yest.toDateString()
  if (isToday) return `Oggi · ${d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}`
  if (isYest) return `Ieri · ${d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}`
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const diff = Date.now() - then
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'adesso'
  if (min < 60) return `${min}min fa`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `oggi · ${hrs}h fa`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'ieri'
  if (days < 7) return `${days}gg fa`
  return new Date(iso).toLocaleDateString('it-IT')
}

function computeInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + (parts[parts.length - 1][0] ?? '')).toUpperCase()
}

function avatarColor(seed: string): string {
  let h = 0
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) % 360
  return `oklch(58% 0.12 ${h})`
}

function SvgWA() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.4A10 10 0 1 0 12 2zm5 14c-.2.6-1.3 1.2-1.8 1.2-.5.1-1 .1-3.3-.7-2.8-1-4.6-3.8-4.7-4-.1-.2-1.1-1.4-1.1-2.7s.7-1.9 1-2.2c.2-.2.5-.2.7-.2h.5c.2 0 .4 0 .6.5l.8 2c.1.2.1.4 0 .5l-.3.5c-.1.2-.2.3-.4.5-.1.2-.3.3-.1.6.2.3 1 1.6 2 2.4 1.4 1.2 2.5 1.5 2.8 1.7.3.1.5.1.6-.1l.8-1c.1-.2.3-.2.5-.1l1.8.9c.2.1.4.2.5.3 0 .1 0 .8-.3 1.4z" />
    </svg>
  )
}

function SvgMail() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 7 9-7" />
    </svg>
  )
}
